import "@/styles/table.css";

import type { ReactNode } from "react";
import type {
  ColumnFilter,
  FilterFn,
  HeaderContext,
  RowData,
  TableOptions,
  Table as TableType,
} from "@tanstack/react-table";
import {
  filterFns,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import type { SelectOptions } from "@/components/select-multiple-custom";
import CustomPagination from "@/components/table/custom-pagination";
import Loader from "@/components/ui/loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { Filter as FilterType, OrderBy } from "@/types/api/utils";
import {
  datesFilterFn,
  getCellRowSpan,
  resolveData,
  selectFilterFn,
} from "@/utils/data-table";
import { normaliseString } from "@/utils/string";

//allows us to define custom properties for our columns
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?:
      | "text"
      | "select"
      | "checkbox"
      | "switch"
      | "dates"
      | "number-range";
    // Allow complex filters (while using accessorFn for exemple)
    getServerFilter?: (state: ColumnFilter) => FilterType | Array<FilterType>;
    // Allow complex orderBy (while using accessorFn for exemple)
    getServerOrderBy?: (direction: "asc" | "desc") => OrderBy | Array<OrderBy>;
    filterPlaceholder?: string;
    filterOptions?: Array<SelectOptions>;
    filterLabel?: ReactNode;
    enableRowSpan?: boolean;
  }

  interface Cell<TData, TValue> {
    rowSpan?: number;
    isSpanned?: boolean;
  }

  interface TableMeta<TData extends RowData> {
    getServerSelectAllIds?: (
      context: HeaderContext<TData, unknown>
    ) => Promise<Array<number>>;
    [key: string]: unknown;
  }
}

interface DataTableProps<TData> {
  table: TableType<TData>;
  isFilterOpen?: boolean;
  rowClick?: (id: string | number | object) => void;
  isLoading?: boolean;
  isError?: boolean;
  canPreviousPage?: boolean;
  canNextPage?: boolean;
}

const customIncludeString: FilterFn<unknown> = (row, columnId, filterValue) => {
  const search = normaliseString(filterValue);
  return Boolean(
    normaliseString(
      row.getValue<string | null>(columnId)?.toString() ?? ""
    )?.includes(search)
  );
};

export const useTable = <TData,>({
  data,
  columns,
  defaultColumn,
  filterFns: customFilterFns,
  manualPagination,
  pageCount,
  onPaginationChange,
  state,
  ...options
}: Omit<
  TableOptions<TData>,
  | "data"
  | "getCoreRowModel"
  | "getSortedRowModel"
  | "getFacetedUniqueValues"
  | "getFilteredRowModel"
  | "getPaginationRowModel"
> & { data?: Array<TData> }) => {
  const table = useReactTable({
    data: resolveData(data),
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination,
    pageCount,
    onPaginationChange,
    state,
    ...options,
    filterFns: {
      includesString: customIncludeString,
      ...customFilterFns,
    },
    defaultColumn: {
      // Function copied from https://github.com/TanStack/table/blob/6b4d616dd7c8917616eb4fecaf09dda7030fd115/packages/table-core/src/features/ColumnFiltering.ts#L273C1-L274C1
      // Adjusted so that includesString ignores accents
      filterFn: (...args) => {
        const columnId = args[1];
        const firstRow = table.getCoreRowModel().flatRows[0];

        const value = firstRow?.getValue(columnId);
        const filterValue = args[2];

        if (Array.isArray(filterValue)) {
          if (typeof value === "number")
            throw new Error(
              `Auto Filter cannot decide between number range and select filter for ${columnId}, please precise column filterFn prop.`
            );
          return selectFilterFn(...args);
        }

        if (
          typeof filterValue === "object" &&
          "from" in filterValue &&
          "to" in filterValue
        ) {
          return datesFilterFn(...args);
        }

        if (typeof value === "string") {
          // Adjusting for accent supports
          return customIncludeString(...args);
        }

        if (typeof value === "number") {
          const [min, max] = filterValue;
          const argsCopy: typeof args = [...args];
          argsCopy[2] = [min ?? -Infinity, max ?? Infinity];
          return filterFns.inNumberRange(...argsCopy);
        }

        if (typeof value === "boolean") {
          return filterFns.equals(...args);
        }
        if (value !== null && typeof value === "object") {
          return filterFns.equals(...args);
        }

        if (Array.isArray(value)) {
          return filterFns.arrIncludes(...args);
        }

        return filterFns.weakEquals(...args);
      },
      ...defaultColumn,
    },
  });
  return table;
};

export function DataTable<TData>({
  table,
  rowClick,
  isLoading = false,
  isError,
  canNextPage = false,
  canPreviousPage = false,
}: DataTableProps<TData>) {
  const pageIndex = table.getState().pagination.pageIndex;
  const columns = table.getAllColumns();

  const rows = table.getRowModel().rows;

  return (
    <div>
      <div className="-mx-2 sm:-mx-8 lg:mx-0 lg:rounded-md lg:border overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-brand-50">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <TableRow className="hover:bg-white">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <Loader />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow className="hover:bg-white">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Erreur lors du chargement des données.
                </TableCell>
              </TableRow>
            ) : rows?.length ? (
              rows.map((row, rowIndex) => {
                const visibleCells = row.getVisibleCells();
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => {
                      if (window.getSelection()?.toString()) return;
                      if (
                        rowClick &&
                        (typeof row.id === "number" ||
                          typeof row.id === "string" ||
                          typeof row.id === "object") &&
                        row.id !== null
                      ) {
                        rowClick(row.id);
                      }
                    }}
                  >
                    {visibleCells.map((cell, index) => {
                      const rowSpan = getCellRowSpan({
                        cell,
                        previousCells:
                          index > 0 ? visibleCells.slice(0, index) : undefined,
                        rows,
                        rowIndex,
                      });
                      if (rowSpan === null) return null;
                      return (
                        <TableCell
                          key={cell.id}
                          rowSpan={rowSpan}
                          className={cn({
                            "cursor-pointer": !!rowClick,
                            "align-top pointer-events-none bg-background":
                              !!rowSpan,
                          })}
                        >
                          {cell.getIsAggregated()
                            ? flexRender(
                                cell.column.columnDef.aggregatedCell ??
                                  cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            : flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="hover:bg-white">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Pas de résultats.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {canPreviousPage || canNextPage ? (
        <CustomPagination
          pageIndex={pageIndex}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          setPageIndex={table.setPageIndex}
        />
      ) : null}
    </div>
  );
}
